<template>
  <div class="my-l p-0" :class="{ container: !fullWidth }">
    <h2 v-if="mainHeading" class="fluid-xl leading-tight font-medium my-5" data-test-id="main-heading">
      {{ mainHeading }}
    </h2>
    <div
      class="sm:flex-row relative flex flex-col"
      :class="{
        'sm:flex-row-reverse': imageRight,
      }"
    >
      <FixedAspect :ratio="2 / 3" class="sm:hidden w-full">
        <CldnImg
          v-if="image && image[0]"
          :src="image[0].url"
          width="720"
          crop="fill"
          :alt="image[0].title"
          sizes="100vw sm:100vw md:100vw lg:100vw xl:100vw 2xl:100vw"
        />
      </FixedAspect>
      <FixedAspect v-if="useCompactLayout" :ratio="3 / 4" :h-full="true" class="sm:block hidden w-1/3">
        <CldnImg
          v-if="image && image[0]"
          :src="image[0].url"
          :alt="image[0].title"
          crop="fill"
          sizes="100vw md:340px md:480px 2xl:650px"
        />
      </FixedAspect>
      <div v-else class="sm:block hidden w-1/2">
        <CldnImg
          v-if="image && image[0]"
          :src="image[0].url"
          :alt="image[0].title"
          crop="fill"
          class="background-image object-cover h-full w-full"
          sizes="100vw md:340px md:480px 2xl:650px"
          :ratio="5 / 6"
        />
      </div>
      <div
        class="px-6 md:px-10 py-6 flex flex-col items-start justify-center w-full"
        :class="{
          'bg-putty text': backgroundGray,
          'bg-blackly text-white': !backgroundGray,
          'sm:w-1/2': !useCompactLayout,
          'sm:w-2/3': useCompactLayout,
        }"
      >
        <h2
          v-if="primaryHeading"
          class="leading-none"
          :class="{
            'fluid-3xl': !useCompactLayout,
            'fluid-lg': useCompactLayout,
          }"
          data-testid="heading"
          v-html="primaryHeading"
        ></h2>

        <h3 v-if="subtitle" class="fluid-lg leading-tight font-medium mt-3 text__bg--highlight" v-html="subtitle"></h3>

        <RoutineFeatures
          v-if="routine && routine.length > 0"
          class="mt-5"
          :duration="routineDuration"
          :exercise-count="routineExerciseCount"
          :difficulty="routineDifficulty"
        ></RoutineFeatures>

        <div
          v-if="body"
          class="sm:mt-6 text-copy checkmark-bullets mt-4 font-light leading-normal"
          :class="{
            'fluid-lg': !useCompactLayout,
            'fluid-base': useCompactLayout,
          }"
          v-html="body"
        ></div>
        <LinkField
          v-if="cta[0] && cta[0].text"
          v-bind="cta[0]"
          type-name="FiftyFiftyTeaser"
          :tracking-name="heading"
          :block-position="blockPosition"
          class="sm:mt-6 inline-block mt-4 btn-pill-black"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import FixedAspect from '@/components/common/FixedAspect';
import CldnImg from '@/components/CldnImg';
import LinkField from '@/components/common/LinkField';
import RoutineFeatures from '@/components/exercises/RoutineFeatures';
import { useRoutine } from '@/composables/useRoutine';

const props = defineProps({
  cta: {
    type: Array,
    default: () => [],
  },
  routine: {
    type: Array,
    default: () => [],
  },
  mainHeading: {
    type: String,
    default: null,
  },
  heading: {
    type: String,
    default: '',
  },
  subtitle: {
    type: String,
    default: null,
  },
  image: {
    type: Array,
    default: () => [],
  },
  body: {
    type: String,
    default: '',
  },
  imageRight: {
    type: Boolean,
    default: false,
  },
  backgroundGray: {
    type: Boolean,
    default: false,
  },
  useCompactLayout: {
    type: Boolean,
    default: false,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  blockPosition: {
    type: Number,
    default: 0,
  },
});

const primaryHeading = computed(() => {
  if (props.heading) {
    return props.heading;
  }

  if (routineTitle.value) {
    return routineTitle.value;
  }

  return '';
});

const { routineDuration, routineExerciseCount, routineDifficulty, routineTitle } = useRoutine(props.routine[0] ?? null);
</script>

<style scoped>
@reference "~/assets/css/main.css";
.box {
  @apply py-8 sm:py-12 xl:px-24;
}
</style>
